import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CTGeneralService,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator,
  MAT_RAISED_PRIMARY
} from "@ctsolution/ct-framework";
import { DEFAULT_DATATABLE_CONFIGURATION } from "../../_core/constants";
import { CTMGeneralService } from '../../_core/lib/general.service';
import { CtManagerService } from '../../_core/lib/ct-manager.service';
import { JwtService } from '../../_core/lib/jwt.service';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest, MethodEnum } from "@ctsolution/ct-webapi";
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { UserRouteData } from '../../_core/route-data/user.route-data';
import { UserInfoFormTemplateRouteData } from '../../_core/route-data/user-info-form-template.route-data';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {

  profileForm: FormGroup;
  isLoading = true;
  isSaving = false;
  userOid: number | null = null;
  originalProfileData: any = null;

  /** Tabella "I miei moduli", model-driven (ct-model su UserInfo_FormTemplate). */
  modulesConfiguration: CtModelConfiguration<any> | null = null;

  /**
   * Solo Semi di Senape: i moduli vengono suddivisi in tab per FormTemplate.Section.
   * Ogni tab è la stessa tabella filtrata per sezione; l'ultima ("Altri") mostra i moduli
   * senza sezione (Section null).
   */
  useSectionTabs = (environment.projectName ?? '').startsWith('SemiDiSenape');

  /**
   * Sezioni fisse richieste dal cliente (in ordine).
   * `label` = testo mostrato nella tab; `section` = valore di FormTemplate.Section usato nel filtro
   * (deve combaciare con quello salvato lato BE). Nella maggior parte dei casi coincidono.
   */
  private readonly MODULE_SECTIONS: { label: string; section: string }[] = [
    { label: 'Codice etico e modello 231', section: 'Codice etico e modello 231' },
    { label: 'Documenti generali', section: 'Documenti generali' },
    { label: 'Documenti assunzione', section: 'Documenti assunzione' },
    { label: 'Protocolli e procedure', section: 'Protocolli e procedure' },
    { label: 'Sicurezza sul lavoro', section: 'Sicurezza sul lavoro' }
  ];

  /** Tab dei moduli per sezione (valorizzato solo se useSectionTabs). */
  modulesTabs: { label: string; configuration: CtModelConfiguration<any> }[] = [];

  /** Template della cella "Visionato": pallino verde (visionato) / rosso (non visionato). */
  @ViewChild('viewedCell', { static: true }) viewedCellTpl: TemplateRef<any> | null = null;

  saveButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel("Salva modifiche")
    .setAction(() => this.submit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  constructor(
    private CTGeneralService: CTGeneralService,
    private generalService: CTMGeneralService,
    private jwtService: JwtService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private webapi: CtWebapiService,
    private managerService: CtManagerService,
    private router: Router
  ) {
    this.profileForm = this.formBuilder.group({
      Name: ['', Validators.required],
      Surname: ['', Validators.required]
    });
  }

  async ngOnInit() {
    await this.loadProfile();
  }

  async loadProfile() {

    this.isLoading = true;

    try {

      const userOid = await this.jwtService.getUserInfoOid();

      if (!userOid) {
        console.error('Impossibile recuperare l\'OID dell\'utente');
        this.isLoading = false;
        return;
      }

      this.userOid = +userOid;

      // Moduli dell'utente loggato: solo se la feature "Modules" è abilitata in SiteConfiguration.
      if (await this.managerService.hasEnabled.modules()) {
        this.loadModules(this.userOid);
      }

      const response: CtWebapiGenericResponse<any> = await this.generalService
        .getInfos({
          controller: 'User',
          oid: this.userOid
        })
        .toPromise();

      const profileData = response?.Result?.DataSource;

      if (profileData) {
        this.originalProfileData = profileData;

        this.profileForm.patchValue({
          Name: profileData.Name || '',
          Surname: profileData.Surname || ''
        });
      }

    } catch (error) {
      console.error('Errore durante il caricamento del profilo:', error);
    } finally {
      this.isLoading = false;
    }

  }

  /**
   * Carica i moduli dell'utente loggato tramite l'action dedicata del backend:
   * POST UserInfo_FormTemplate/OwnedOnlyList (come la lista normale, ma solo i propri).
   * La sezione "I miei moduli" appare solo se c'è almeno un modulo.
   */
  private loadModules(userInfoOid: number): void {

    // Semi di Senape: una tab per sezione + tab "Altri" (moduli senza sezione).
    if (this.useSectionTabs) {

      this.modulesTabs = this.MODULE_SECTIONS.map(s => ({
        label: s.label,
        configuration: this.buildModulesConfig(userInfoOid, { section: s.section })
      }));

      return;
    }

    // Default: tabella unica.
    this.modulesConfiguration = this.buildModulesConfig(userInfoOid);

  }

  /**
   * Costruisce la configurazione ct-model dei moduli, filtrata sull'utente loggato e,
   * opzionalmente, per sezione (FormTemplate.Section == valore) oppure per assenza di
   * sezione (FormTemplate.Section is null → tab "Altri").
   */
  private buildModulesConfig(
    userInfoOid: number,
    sectionFilter?: { section?: string; noSection?: boolean }
  ): CtModelConfiguration<any> {

    // Tabella model-driven (ct-model) su UserInfo_FormTemplate, come le altre liste del progetto.
    // Filtrata sull'utente loggato; azione di riga "Dettaglio" → apre il modulo.
    const config = CtModelConfiguration
      .create()
      .setRouteData(UserInfoFormTemplateRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setOperations([CtModelOperator.Detail])
      // Evita che la tabella moduli sovrascriva il titolo/breadcrumb della pagina
      // col nome del controller (es. "userinfo_formtemplate").
      .setAutoSetupBreadcrumbs(false)
      .setOnDetail((row: any) => this.openModule(row));

    config
      .filterValues
      .addConstantFilter(
        CTModelDatatableFilter
          .create()
          .setField('UserInfo.Oid')
          .setValue(userInfoOid)
          .setOperatorType(CtBinaryOperator.Equal));

    // Filtro per sezione (tab Semi di Senape).
    if (sectionFilter?.section) {
      config
        .filterValues
        .addConstantFilter(
          CTModelDatatableFilter
            .create()
            .setField('FormTemplate.Section')
            .setValue(sectionFilter.section)
            .setOperatorType(CtBinaryOperator.Equal));
    } else if (sectionFilter?.noSection) {
      // Moduli senza sezione: espressione raw "is null" (come altrove nel progetto).
      config
        .filterValues
        .addConstantFilter(
          CTModelDatatableFilter
            .create()
            .setField('[FormTemplate.Section] is null')
            .setValue([])
            .setOperatorType(CtBinaryOperator.AndOperator));
    }

    // Colonna "Data visione": mostra la data se valorizzata, altrimenti un pallino rosso.
    if (this.viewedCellTpl) {
      config.setFieldsCustomTemplates([
        { prop: 'ReadCompletedDate', cellTemplate: this.viewedCellTpl }
      ]);
    }

    // Solo lettura: il merge col backend (getFields) fa un push in-place su Operations e
    // può reintrodurre 'Create'. Ripristiniamo il set voluto dopo l'inizializzazione.
    config
      .initialized()
      .subscribe(() => config.setOperations([CtModelOperator.Detail]));

    return config;

  }

  /**
   * Apre il dettaglio del modulo, passando l'Oid del template e quello dell'associazione
   * (uftOid) che serve al dettaglio per valorizzare ReadCompletedDate al salvataggio.
   */
  openModule(row: any): void {

    const templateOid = row?.FormTemplate?.Oid ?? row?.templateOid;
    const uftOid = row?.Oid ?? row?.uftOid;
    if (!templateOid) return;

    this.router.navigate(['/', 'profile', 'form-template', templateOid], {
      queryParams: { uftOid }
    });

  }

  async submit() {

    this.profileForm.markAllAsTouched();

    if (this.profileForm.valid) {

      this.isSaving = true;

      const formValues = this.profileForm.getRawValue();

      const dataToSend = {
        ...this.originalProfileData,
        Name: formValues.Name,
        Surname: formValues.Surname
      };

      const configuration = CtModelConfiguration
        .create()
        .setRouteData(
          UserRouteData().setId(this.userOid!)
        );

      try {

        await this.generalService.putData(configuration, dataToSend);

        this.showSuccessMessage('Profilo aggiornato con successo');

        await this.loadProfile();

      } catch (error) {
        console.error('Errore durante il salvataggio:', error);
        this.showErrorMessage('Errore durante il salvataggio del profilo');
      } finally {
        this.isSaving = false;
      }

    }

  }

  hasError(fieldName: string, errorType: string): boolean {
    const field = this.profileForm.get(fieldName);
    return !!(field?.hasError(errorType) && (field?.dirty || field?.touched));
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 7000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['error-snackbar']
    });
  }

}
